import { useMemo } from 'react';
import { FaxNumber } from '@weave/schema-gen-ts/dist/schemas/fax/v1/fax.pb';
import { FaxQueries } from '@frontend/api-fax';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useScopedAppFlagStore } from '@frontend/scope';

type UseAccessibleFaxNumbersOptions = {
  enabled?: boolean;
};

/**
 * Hook that returns the fax-capable phone numbers for the given locations.
 *
 * When the `fax-user-phone-access-control` flag is enabled, the data is sourced from
 * the ACL-aware `ListUserAccessiblePhoneNumbers` endpoint and filtered to
 * numbers with the `faxProvider` capability — so users only see fax numbers
 * they have permission to access.
 *
 * When the flag is disabled (or undefined), it falls back to the legacy
 * `FaxQueries.useQueryFaxNumbers` (`ListNumbers`) endpoint which returns every
 * fax-provisioned number on the selected locations.
 *
 * Both paths return the same `FaxNumber[]` shape (`{ locationPhone, displayName }`).
 */
export const useAccessibleFaxNumbers = (
  locationIds: string[],
  { enabled = true }: UseAccessibleFaxNumbersOptions = {}
) => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const privateInboxEnabled = getFeatureFlagValue('fax-user-phone-access-control', 'ALL');

  const accessibleQuery = PhoneNumbersV1.Queries.useListUserAccessiblePhoneNumbersQuery({
    request: { locationIds },
    options: {
      enabled: enabled && privateInboxEnabled && locationIds.length > 0,
    },
  });

  const legacyQuery = FaxQueries.useQueryFaxNumbers(locationIds, {
    enabled: enabled && !privateInboxEnabled,
  });

  const accessibleData = useMemo<FaxNumber[]>(() => {
    if (!accessibleQuery.data?.phoneNumbers) return [];

    return accessibleQuery.data.phoneNumbers.reduce<FaxNumber[]>((acc, curr) => {
      if (!curr.capabilities?.faxProvider) return acc;

      const nationalNumber = curr.phoneNumber?.nationalNumber;
      if (nationalNumber === undefined) return acc;

      // need to coerce to a string because FaxNumber is string and nationalNumber is number.
      const locationPhone = formatPhoneNumberE164(String(nationalNumber));
      if (!locationPhone) return acc;

      acc.push({
        locationPhone,
        displayName: curr.displayName,
      });
      return acc;
    }, []);
  }, [accessibleQuery.data?.phoneNumbers]);

  if (privateInboxEnabled) {
    return {
      ...accessibleQuery,
      data: accessibleData,
    };
  }

  return {
    ...legacyQuery,
    data: legacyQuery.data ?? [],
  };
};
